import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { OrbitAuthProvider, useOrbitAuth } from './context/OrbitAuthContext';
import { Toaster } from './components/ui/sonner';
import { SiteGate } from './components/SiteGate';

// Pages - Auth
import OrbitLoginPage from './pages/OrbitLoginPage';
import OrbitRegisterPage from './pages/OrbitRegisterPage';

// Pages - Member Dashboard
import OrbitDashboardPage from './pages/OrbitDashboardPage';
import OrbitMatrixPage from './pages/OrbitMatrixPage';
import OrbitWalletPage from './pages/OrbitWalletPage';
import OrbitCommissionsPage from './pages/OrbitCommissionsPage';
import MyTeamPage from './pages/MyTeamPage';
import BusinessRegisterPage from './pages/BusinessRegisterPage';
import LocalSitePassPage from './pages/LocalSitePassPage';
import MemberDirectoryPage from './pages/MemberDirectoryPage';
import MyProfilePage from './pages/MyProfilePage';
import MyListingsPage from './pages/MyListingsPage';
import BrowseListingsPage from './pages/BrowseListingsPage';

// Pages - Admin
import OrbitAdminPage from './pages/OrbitAdminPage';
import OrbitCalculatorPage from './pages/OrbitCalculatorPage';
import OrbitAdminSimulatorPage from './pages/OrbitAdminSimulatorPage';
import OrbitExpensesPage from './pages/OrbitExpensesPage';
import OrbitCashFlowPage from './pages/OrbitCashFlowPage';
import AdminNotesPage from './pages/AdminNotesPage';
import AdminDirectoryPage from './pages/AdminDirectoryPage';

// Pages - Super Admin
import SuperAdminLoginPage from './pages/SuperAdminLoginPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';

// Pages - Public
import AffiliationProgramPage from './pages/AffiliationProgramPage';

// Pages - Directory (LocalSite.Club)
import DirectoryLandingPage from './pages/DirectoryLandingPage';
import DirectoryBrowsePage from './pages/DirectoryBrowsePage';
import BusinessDetailPage from './pages/BusinessDetailPage';

// Protected Route — Member must be logged in
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useOrbitAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E17]">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Public Route — Redirect logged-in users to dashboard
const PublicRoute = ({ children }) => {
  const { user, loading } = useOrbitAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0E17]">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRouter() {
  return (
    <Routes>
      {/* LocalSite.Club Directory Routes (Public) */}
      <Route path="/" element={<DirectoryLandingPage />} />
      <Route path="/browse" element={<DirectoryBrowsePage />} />
      <Route path="/business/:slug" element={<BusinessDetailPage />} />
      <Route path="/affiliation" element={<AffiliationProgramPage />} />

      {/* Super Admin Routes (Secret URL — separate login) */}
      <Route path="/super-admin" element={<SuperAdminLoginPage />} />
      <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />

      {/* Auth Routes */}
      <Route path="/login" element={<PublicRoute><OrbitLoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><OrbitRegisterPage /></PublicRoute>} />

      {/* Member Dashboard Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><OrbitDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard/matrix" element={<ProtectedRoute><OrbitMatrixPage /></ProtectedRoute>} />
      <Route path="/dashboard/wallet" element={<ProtectedRoute><OrbitWalletPage /></ProtectedRoute>} />
      <Route path="/dashboard/commissions" element={<ProtectedRoute><OrbitCommissionsPage /></ProtectedRoute>} />
      <Route path="/dashboard/team" element={<ProtectedRoute><MyTeamPage /></ProtectedRoute>} />
      <Route path="/dashboard/business" element={<ProtectedRoute><BusinessRegisterPage /></ProtectedRoute>} />
      <Route path="/dashboard/pass" element={<ProtectedRoute><LocalSitePassPage /></ProtectedRoute>} />
      <Route path="/dashboard/members" element={<ProtectedRoute><MemberDirectoryPage /></ProtectedRoute>} />
      <Route path="/dashboard/profile" element={<ProtectedRoute><MyProfilePage /></ProtectedRoute>} />
      <Route path="/dashboard/listings" element={<ProtectedRoute><MyListingsPage /></ProtectedRoute>} />
      <Route path="/dashboard/listings/browse" element={<ProtectedRoute><BrowseListingsPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<Navigate to="/admin/finances" replace />} />
      <Route path="/admin/calculator" element={<ProtectedRoute><OrbitCalculatorPage /></ProtectedRoute>} />
      <Route path="/admin/finances" element={<ProtectedRoute><OrbitAdminPage /></ProtectedRoute>} />
      <Route path="/admin/simulator" element={<ProtectedRoute><OrbitAdminSimulatorPage /></ProtectedRoute>} />
      <Route path="/admin/expenses" element={<ProtectedRoute><OrbitExpensesPage /></ProtectedRoute>} />
      <Route path="/admin/cashflow" element={<ProtectedRoute><OrbitCashFlowPage /></ProtectedRoute>} />
      <Route path="/admin/notes" element={<ProtectedRoute><AdminNotesPage /></ProtectedRoute>} />
      <Route path="/admin/directory" element={<ProtectedRoute><AdminDirectoryPage /></ProtectedRoute>} />

      {/* Legacy redirects — old /orbit/* URLs redirect to new paths */}
      <Route path="/orbit/login" element={<Navigate to="/login" replace />} />
      <Route path="/orbit/register" element={<Navigate to="/register" replace />} />
      <Route path="/orbit/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/orbit/matrix" element={<Navigate to="/dashboard/matrix" replace />} />
      <Route path="/orbit/wallet" element={<Navigate to="/dashboard/wallet" replace />} />
      <Route path="/orbit/commissions" element={<Navigate to="/dashboard/commissions" replace />} />
      <Route path="/orbit/team" element={<Navigate to="/dashboard/team" replace />} />
      <Route path="/orbit/calculator" element={<Navigate to="/admin/calculator" replace />} />
      <Route path="/orbit/admin" element={<Navigate to="/admin/finances" replace />} />
      <Route path="/orbit/admin/simulator" element={<Navigate to="/admin/simulator" replace />} />
      <Route path="/orbit/admin/expenses" element={<Navigate to="/admin/expenses" replace />} />
      <Route path="/orbit/admin/cashflow" element={<Navigate to="/admin/cashflow" replace />} />
      <Route path="/orbit/business" element={<Navigate to="/dashboard/business" replace />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <OrbitAuthProvider>
        <BrowserRouter>
          <SiteGate>
            <AppRouter />
          </SiteGate>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </OrbitAuthProvider>
    </LanguageProvider>
  );
}

export default App;
